import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Drawer,
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
  Grid,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { FormatBold, FormatItalic, Title } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Swal from "sweetalert2";

import axiosInstance from "../../Services/axiosConfig";
import { useThemeMode } from "../../components/ThemeModeContext"; 
import Sidebar from "../../components/CompanySidebar";
import Footer from "../../components/Footer";
import FeedbackButton from "../../components/FeedbackButton";
import FeedbackSnackbar from "../../components/FeedbackSnackbar";

export default function ManageSectors() {
  const { companyId } = useParams();
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentSector, setCurrentSector] = useState(null);

  const token = localStorage.getItem("authToken");

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    open_days: {
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false,
    },
    schedule: {},
    lunch_breaks: {},
    holidays: "",
  });

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const { mode } = useThemeMode();
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          background: {
            default: mode === "light" ? "#FAF5E4" : "#121212",
            paper: mode === "light" ? "#FFFFFF" : "#1E1E1E",
          },
          text: {
            primary: mode === "light" ? "#1A202C" : "#FFFFFF",
          },
        },
      }),
    [mode]
  );

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const fetchSectors = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`sectors/${token}/${companyId}/`);
      if (response.data.status === "success") {
        const sectorList = Object.entries(response.data.data).map(([id, sector]) => ({
          id,
          ...sector,
        }));
        setSectors(sectorList);
      }
    } catch (error) {
      console.error("Error fetching sectors", error);
      setOpenSnackbar(true);
      setSnackbarMsg("Error fetching sectors.");
      setSnackbarSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSectors();
  }, [token, companyId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        open_days: { ...prev.open_days, [name]: checked },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleScheduleChange = (day, type, value) => {
    setFormData((prev) => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [day]: { ...prev.schedule[day], [type]: value },
      },
    }));
  };

  const handleLunchBreakChange = (day, type, value) => {
    setFormData((prev) => ({
      ...prev,
      lunch_breaks: {
        ...prev.lunch_breaks,
        [day]: { ...prev.lunch_breaks[day], [type]: value },
      },
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const endpoint = editMode
        ? `edit-sector/${token}/${companyId}/${currentSector.id}/`
        : `create-sector/${token}/${companyId}/`;

      const response = await axiosInstance.post(endpoint, {
        ...formData,
        holidays: formData.holidays.split(",").map((date) => date.trim()), // Converte string para array
      });

      if (response.data.status === "success") {
        fetchSectors();
        setDrawerOpen(false);
      } else {
        Swal.fire("Error", response.data.message, "error");
      }
    } catch (error) {
      console.error("Error saving sector", error);
      Swal.fire("Error", "An error occurred. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box display="flex" flexDirection="column" minHeight="100vh" bgcolor="background.default">
        <Box display="flex" flexGrow={1}>
          <Sidebar companyId={companyId} />

          <Box flex={1} p={3} color="text.primary">
            <Typography variant="h4" gutterBottom>
              Manage Sectors
            </Typography>

            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setFormData({
                  name: "",
                  description: "",
                  open_days: {},
                  schedule: {},
                  lunch_breaks: {},
                  holidays: "",
                });
                setEditMode(false);
                setDrawerOpen(true);
              }}
              sx={{ mb: 2 }}
            >
              Create Sector
            </Button>

            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" mt={3}>
                <CircularProgress />
              </Box>
            ) : (
              <Box mt={3}>
                {sectors.map((sector) => (
                  <Box
                    key={sector.id}
                    p={2}
                    border={1}
                    borderColor="grey.300"
                    borderRadius={2}
                    mb={2}
                  >
                    <Typography variant="h6">{sector.name}</Typography>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {sector.description}
                    </ReactMarkdown>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>

        <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
          <Box width={400} p={3} role="presentation" bgcolor="background.default" color="text.primary">
            <Typography id="drawer-title" variant="h6" gutterBottom>
              {editMode ? "Edit Sector" : "Create Sector"}
            </Typography>

            <TextField label="Sector Name" name="name" value={formData.name} onChange={handleChange} fullWidth required margin="normal" />

            {Object.keys(formData.open_days).map((day) => (
              <FormControlLabel
                key={day}
                control={<Checkbox name={day} checked={formData.open_days[day]} onChange={handleChange} />}
                label={day.charAt(0).toUpperCase() + day.slice(1)}
              />
            ))}

            <TextField label="Holidays (comma-separated)" name="holidays" value={formData.holidays} onChange={handleChange} fullWidth margin="normal" />

            <Button variant="contained" color="primary" onClick={handleSubmit} disabled={loading} fullWidth sx={{ mt: 2 }}>
              {loading ? <CircularProgress size={20} color="inherit" /> : "Save"}
            </Button>
          </Box>
        </Drawer>

        <FeedbackButton />
        <Footer />
      </Box>
    </ThemeProvider>
  );
}
